/**
 * Upload Progress Component
 * 
 * Shows real-time progress for chunked file uploads.
 * Displays individual file status and overall progress.
 * Includes live timer for upload duration tracking.
 */

import React, { useState, useEffect } from 'react';
import './UploadProgress.css';

const FileStatus = {
  PENDING: 'pending',
  UPLOADING: 'uploading',
  COMPLETE: 'complete',
  ERROR: 'error'
};

const StatusIcon = ({ status }) => {
  switch (status) {
    case FileStatus.COMPLETE:
      return <span className="status-icon complete">✅</span>;
    case FileStatus.UPLOADING:
      return <span className="status-icon uploading">🔄</span>;
    case FileStatus.ERROR:
      return <span className="status-icon error">❌</span>;
    case FileStatus.PENDING:
    default:
      return <span className="status-icon pending">⏳</span>;
  }
};

// Live Timer Component
const LiveTimer = ({ startTime }) => {
  const [elapsed, setElapsed] = useState(0);
  
  useEffect(() => {
    if (!startTime) return;
    
    const updateElapsed = () => setElapsed(Date.now() - startTime);
    updateElapsed();
    const interval = setInterval(updateElapsed, 1000);
    return () => clearInterval(interval);
  }, [startTime]);
  
  const formatDuration = (ms) => {
    if (!ms || ms < 0) return '0s';
    const seconds = Math.floor(ms / 1000);
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds}s`;
  };
  
  return (
    <span className="upload-timer">⏱️ {formatDuration(elapsed)}</span>
  );
};

export default function UploadProgress({ 
  files = [], 
  fileStatuses = {}, 
  progress = { completed: 0, total: 0, percent: 0 },
  isUploading = false,
  error = null,
  onCancel,
  startTime = null
}) {
  const { completed, total, percent } = progress;
  const [uploadStartTime] = useState(() => startTime || Date.now());

  if (!isUploading && total === 0) {
    return null;
  }

  return (
    <div className="upload-progress-overlay">
      <div className="upload-progress-modal">
        <div className="upload-progress-header">
          <h3>📤 Uploading Documents</h3>
          <div className="upload-header-right">
            <LiveTimer startTime={uploadStartTime} />
            <span className="upload-count">{completed}/{total}</span>
          </div>
        </div>

        {error && (
          <div className="upload-error">
            <span>❌ {error}</span>
          </div>
        )}

        <div className="upload-progress-bar-container">
          <div 
            className="upload-progress-bar" 
            style={{ width: `${percent}%` }}
          />
          <span className="upload-progress-percent">{percent}%</span>
        </div>

        <div className="upload-files-list">
          {files.map((file, index) => {
            const status = fileStatuses[file.name] || FileStatus.PENDING;
            return (
              <div 
                key={`${file.name}-${index}`} 
                className={`upload-file-item ${status}`}
              >
                <StatusIcon status={status} />
                <span className="file-name" title={file.name}>
                  {file.name.length > 35 
                    ? `${file.name.substring(0, 32)}...` 
                    : file.name
                  }
                </span>
                <span className="file-size">
                  {(file.size / 1024 / 1024).toFixed(2)} MB
                </span>
              </div>
            );
          })}
        </div>

        {isUploading && onCancel && (
          <div className="upload-actions">
            <button 
              className="btn-cancel" 
              onClick={onCancel}
            >
              Cancel
            </button>
          </div>
        )}

        {!isUploading && percent === 100 && (
          <div className="upload-complete-message">
            <span>✅ All files uploaded! Starting extraction...</span>
          </div>
        )}
      </div>
    </div>
  );
}

export { FileStatus };
